import { createJSONStorage, devtools, persist } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { shallow } from "zustand/shallow";
import { createWithEqualityFn } from "zustand/traditional";

import { getIsProd } from "../config";

export type CustomerVerificationStep =
    | "CustomerHandoff"
    | "CustomerPhoneInput"
    | "CustomerNameSelect"
    | "VerificationComplete";

export type CustomerVerificationInitialStep = "CustomerHandoff" | "CustomerPhoneInput";

const defaultInitialStep: CustomerVerificationStep = "CustomerPhoneInput";

interface VerifiedCustomerState {
    stepHistory: CustomerVerificationStep[];
    currentStep: CustomerVerificationStep;
}

interface VerifiedCustomerStore {
    verifiedCustomers: Record<string, VerifiedCustomerState>;
    getCurrentStep: (sessionId: string) => CustomerVerificationStep;
    getStepHistory: (sessionId: string) => CustomerVerificationStep[];
    pushStep: (sessionId: string, step: CustomerVerificationStep) => void;
    popStep: (sessionId: string) => void;
    resetSteps: (sessionId: string) => void;
    setInitialStep: (sessionId: string, initialStep: CustomerVerificationInitialStep) => void;
}

export const useVerificationFlowStore = createWithEqualityFn<VerifiedCustomerStore>()(
    persist(
        immer(
            devtools(
                (set, get) => ({
                    verifiedCustomers: {},
                    getCurrentStep: (sessionId: string) =>
                        get().verifiedCustomers[sessionId]?.currentStep ?? defaultInitialStep,
                    getStepHistory: (sessionId: string) =>
                        get().verifiedCustomers[sessionId]?.stepHistory ?? [defaultInitialStep],
                    pushStep: (sessionId: string, step: CustomerVerificationStep) => {
                        set(
                            (state) => {
                                if (!state.verifiedCustomers[sessionId]) {
                                    state.verifiedCustomers[sessionId] = {
                                        stepHistory: [defaultInitialStep],
                                        currentStep: defaultInitialStep,
                                    };
                                }
                                state.verifiedCustomers[sessionId].stepHistory.push(step);
                                state.verifiedCustomers[sessionId].currentStep = step;
                            },
                            false,
                            "pushStep",
                        );
                    },
                    popStep: (sessionId: string) => {
                        set(
                            (state) => {
                                const customer = state.verifiedCustomers[sessionId];
                                const previousStep = customer?.stepHistory.at(-2);
                                if (customer && previousStep) {
                                    customer.stepHistory.pop();
                                    customer.currentStep = previousStep;
                                }
                            },
                            false,
                            "popStep",
                        );
                    },
                    resetSteps: (sessionId: string) => {
                        set(
                            (state) => {
                                delete state.verifiedCustomers[sessionId];
                            },
                            false,
                            "resetSteps",
                        );
                    },
                    setInitialStep: (sessionId: string, initialStep: CustomerVerificationInitialStep) =>
                        set(
                            (state) => {
                                const existing = state.verifiedCustomers[sessionId];
                                if (existing) {
                                    return;
                                }
                                state.verifiedCustomers[sessionId] = {
                                    stepHistory: [initialStep],
                                    currentStep: initialStep,
                                };
                            },
                            false,
                            "setInitialStep",
                        ),
                }),
                {
                    enabled: !getIsProd(),
                    store: "verification-flow-store",
                    name: "workspace/customer-panel",
                },
            ),
        ),
        {
            name: "verification-flow-store",
            skipHydration: true,
            storage: createJSONStorage(() => ({
                getItem: (key) => globalThis.localStorage.getItem(key),
                // persist saves every `set`, hydrated or not. On a page refresh the verification form mounts
                // before Workspace rehydrates this store, and its `setInitialStep` would overwrite the saved
                // history: `VerificationComplete` was lost, the expert had to verify again, and the eligibility
                // precheck fired meanwhile (expert-workspace#8137). Writes made before hydration stay in memory.
                setItem: (key, value) => {
                    if (useVerificationFlowStore.persist.hasHydrated()) {
                        globalThis.localStorage.setItem(key, value);
                    }
                },
                removeItem: (key) => globalThis.localStorage.removeItem(key),
            })),
            // Per session, so the saved history wins for a session it has (the refreshed call) while a session
            // only started in memory before hydration (a new call) keeps its initial step.
            merge: (persistedState, currentState) => ({
                ...currentState,
                verifiedCustomers: {
                    ...currentState.verifiedCustomers,
                    ...(persistedState as Partial<VerifiedCustomerStore> | undefined)?.verifiedCustomers,
                },
            }),
        },
    ),
    shallow,
);
